import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import IconWrapper from '../common/IconWrapper';
import { FaUserShield, FaEnvelope, FaClock, FaPhone, FaMapMarkerAlt, FaBuilding, FaKey } from 'react-icons/fa';

const ProfileContainer = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
  padding: 2rem;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 700;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  color: #141340;
  margin-bottom: 1rem;
`;

const Form = styled.form`
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: #f8f9fa;
`;

const Button = styled.button`
  background: #141340;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: background 0.2s;
  &:hover {
    background: #F68B21;
  }
`;

const SuccessMsg = styled.div`
  color: #28a745;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const ErrorMsg = styled.div`
  color: #dc3545;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 2rem;
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const Column = styled.div`
  flex: 1;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin: 2rem 0;
`;

const AdminProfile: React.FC = () => {
  const { user } = useAuth();
  const safeUser = user || { contactDetails: {} };
  const [contact, setContact] = useState({
    phone: (safeUser as any).contactDetails?.phone || '',
    address: (safeUser as any).contactDetails?.address || '',
    department: (safeUser as any).contactDetails?.department || '',
  });
  const [contactSuccess, setContactSuccess] = useState('');
  const [contactError, setContactError] = useState('');

  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  if (!user) return null;

  // Handlers
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({ ...contact, [e.target.name]: e.target.value });
  };
  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact.phone || !contact.address || !contact.department) {
      setContactError('All fields are required.');
      setContactSuccess('');
      return;
    }
    setContactSuccess('Contact details updated (mock only).');
    setContactError('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      setPasswordError('All fields are required.');
      setPasswordSuccess('');
      return;
    }
    if (passwords.new.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      setPasswordSuccess('');
      return;
    }
    if (passwords.new !== passwords.confirm) {
      setPasswordError('New passwords do not match.');
      setPasswordSuccess('');
      return;
    }
    setPasswordSuccess('Password changed (mock only).');
    setPasswordError('');
    setPasswords({ current: '', new: '', confirm: '' });
  };

  return (
    <ProfileContainer>
      <ProfileHeader>
        <Avatar>
          <IconWrapper icon={FaUserShield} />
        </Avatar>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#141340' }}>{user.name}</h1>
          <div style={{ color: '#6c757d', fontWeight: 500 }}>System Administrator</div>
        </div>
      </ProfileHeader>
      <FlexRow>
        <Column>
          <SectionTitle>Profile Information</SectionTitle>
          <InfoList>
            <InfoItem>
              <IconWrapper icon={FaEnvelope} />
              <span>{user.email}</span>
            </InfoItem>
            <InfoItem>
              <IconWrapper icon={FaPhone} />
              <span>{contact.phone || 'N/A'}</span>
            </InfoItem>
            <InfoItem>
              <IconWrapper icon={FaMapMarkerAlt} />
              <span>{contact.address || 'N/A'}</span>
            </InfoItem>
            <InfoItem>
              <IconWrapper icon={FaBuilding} />
              <span>{contact.department || 'N/A'}</span>
            </InfoItem>
            <InfoItem>
              <IconWrapper icon={FaClock} />
              <span>Last Login: {(user as any).updatedAt ? new Date((user as any).updatedAt).toLocaleString() : 'N/A'}</span>
            </InfoItem>
            <InfoItem>
              <IconWrapper icon={FaUserShield} />
              <span>Role: {user.role}</span>
            </InfoItem>
          </InfoList>
        </Column>
        <Column>
          <SectionTitle>Edit Contact Details</SectionTitle>
          {contactSuccess && <SuccessMsg>{contactSuccess}</SuccessMsg>}
          {contactError && <ErrorMsg>{contactError}</ErrorMsg>}
          <Form onSubmit={handleContactSubmit} autoComplete="off">
            <FormGroup>
              <Label htmlFor="phone">Phone</Label>
              <Input name="phone" value={contact.phone} onChange={handleContactChange} placeholder="Phone number" />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="address">Address</Label>
              <Input name="address" value={contact.address} onChange={handleContactChange} placeholder="Address" />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="department">Department</Label>
              <Input name="department" value={contact.department} onChange={handleContactChange} placeholder="Department" />
            </FormGroup>
            <Button type="submit">Save Contact Details</Button>
          </Form>
        </Column>
      </FlexRow>
      <Divider />
      <SectionTitle>Change Password</SectionTitle>
      {passwordSuccess && <SuccessMsg>{passwordSuccess}</SuccessMsg>}
      {passwordError && <ErrorMsg>{passwordError}</ErrorMsg>}
      <Form onSubmit={handlePasswordSubmit} autoComplete="off" style={{ maxWidth: 400 }}>
        <FormGroup>
          <Label htmlFor="current">Current Password</Label>
          <Input name="current" type="password" value={passwords.current} onChange={handlePasswordChange} placeholder="Current password" autoComplete="new-password" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="new">New Password</Label>
          <Input name="new" type="password" value={passwords.new} onChange={handlePasswordChange} placeholder="New password" autoComplete="new-password" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="confirm">Confirm New Password</Label>
          <Input name="confirm" type="password" value={passwords.confirm} onChange={handlePasswordChange} placeholder="Confirm new password" autoComplete="new-password" />
        </FormGroup>
        <Button type="submit">Change Password</Button>
      </Form>
    </ProfileContainer>
  );
};

export default AdminProfile; 